import { useEffect, useState, type ReactElement } from "react";
import type { FeatureScreenProps } from "../../../web/src/features/contract.js";

interface Note {
  id: string;
  title: string;
  text: string;
  source: string;
}

/** Admin screen for the example-notes feature: list notes and (if permitted) create one. */
export function ExampleNotesScreen({ actions, call }: FeatureScreenProps): ReactElement {
  const canCreate = actions.some((a) => a.id === "create");
  const [notes, setNotes] = useState<Note[]>([]);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function reload(): Promise<void> {
    try {
      const res = (await call("list", "GET")) as { notes: Note[] };
      setNotes(res.notes ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load notes");
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await call("create", "POST", { title, text });
      setTitle("");
      setText("");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create note");
    }
  }

  return (
    <section aria-labelledby="notes-title">
      <h1 id="notes-title">Example Notes</h1>
      {error ? <p role="alert">{error}</p> : null}
      {canCreate ? (
        <form onSubmit={submit}>
          <label htmlFor="note-title">Title</label>
          <input id="note-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <label htmlFor="note-text">Text</label>
          <textarea id="note-text" value={text} onChange={(e) => setText(e.target.value)} />
          <button type="submit" disabled={!title}>
            Create note
          </button>
        </form>
      ) : null}
      <ul>
        {notes.map((n) => (
          <li key={n.id}>
            <strong>{n.title}</strong> — {n.text} <em>({n.source})</em>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default ExampleNotesScreen;
